import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  Save, X, Upload, FileUp, Loader2, ChevronUp, ChevronDown, Star, Eye, Tag,
} from 'lucide-react';
import PlatformPicker from './PlatformPicker';
import { slugifyClient } from './skillsUtils';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_IMG_SIZE = 10 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_VIDEO_SIZE = 300 * 1024 * 1024;

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  background: 'var(--dp-bg-input, #252525)',
  border: '1px solid var(--dp-border-subtle)',
  borderRadius: '8px',
  color: 'var(--dp-text-primary)',
  fontFamily: 'var(--dp-font)',
  fontSize: '13px',
  outline: 'none',
  transition: 'all 0.3s',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 500,
  color: 'var(--dp-text-secondary)',
  fontFamily: 'var(--dp-font)',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

// Dialog com CustomDropdown dentro: overflow visible no content, scroll no inner
const dialogContentStyleWithDropdown = {
  background: 'var(--dp-bg-glass, rgba(28,28,28,0.85))',
  backdropFilter: 'blur(24px)',
  WebkitBackdropFilter: 'blur(24px)',
  border: '1px solid var(--dp-border-medium)',
  borderRadius: '16px',
  boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
  maxWidth: '640px',
  maxHeight: '85vh',
  overflow: 'visible',
  padding: '0',
};

const dialogInnerStyle = {
  padding: '24px',
  maxHeight: '85vh',
  overflowY: 'auto',
  overflowX: 'hidden',
};

const emptyForm = {
  tipo: '',
  nome: '',
  slug: '',
  descricao: '',
  categoria: '',
  conteudo: '',
  instrucoes: '',
  video_url: '',
  tags: [],
  plataformas: [],
  imagens: [],
  ordem: 1,
  destaque: false,
  publicado: true,
};

/**
 * CRUD de skill do sistema (SuperAdmin). O conteúdo .md é colado ou
 * carregado CLIENT-SIDE (FileReader); só as imagens sobem para o R2.
 * `tipo` é LIVRE (vira o título da seção na página); sugestões vêm dos
 * tipos já existentes via prop existingTipos. presetTipo pré-preenche o
 * tipo ao criar a partir do "+" de uma seção.
 */
const SkillAdminModal = ({ editing, presetTipo = null, existingTipos = [], onClose, onSaved }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptyForm);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const mdInputRef = useRef(null);
  const imgInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Carregar detalhe (com conteudo) ao editar; ordem default ao criar.
  useEffect(() => {
    if (editing) {
      setLoadingDetail(true);
      axios.get(`${API}/skills/${editing.id}`)
        .then((res) => {
          const s = res.data;
          setForm({
            tipo: s.tipo || '',
            nome: s.nome || '',
            slug: s.slug || '',
            descricao: s.descricao || '',
            categoria: s.categoria || '',
            conteudo: s.conteudo || '',
            instrucoes: s.instrucoes || '',
            video_url: s.video_url || '',
            tags: s.tags || [],
            plataformas: s.plataformas || [],
            imagens: s.imagens || [],
            ordem: s.ordem ?? 0,
            destaque: !!s.destaque,
            publicado: s.publicado !== false,
          });
        })
        .catch(() => {
          toast.error(t('skills.admin.error_load'));
          onClose();
        })
        .finally(() => setLoadingDetail(false));
    } else {
      setForm({ ...emptyForm, tipo: presetTipo || '' });
      if (presetTipo) fetchNextOrdem(presetTipo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  const fetchNextOrdem = (tipo) => {
    const cleaned = (tipo || '').trim();
    if (!cleaned) return;
    axios.get(`${API}/skills/next-ordem?tipo=${encodeURIComponent(cleaned)}`)
      .then((res) => setForm((prev) => ({ ...prev, ordem: res.data.next_ordem || 1 })))
      .catch(() => {});
  };

  const handlePickTipo = (tipo) => {
    setForm((prev) => ({ ...prev, tipo }));
    if (!editing) fetchNextOrdem(tipo);
  };

  const addTag = () => {
    const val = tagInput.trim();
    if (!val) return;
    if (!form.tags.includes(val)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, val] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((tg) => tg !== tag) }));
  };

  // Upload do .md lido client-side: preenche o textarea, nada vai ao servidor.
  const handleMdFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setForm((prev) => ({ ...prev, conteudo: String(reader.result || '') }));
      toast.success(t('skills.admin.md_loaded'));
    };
    reader.readAsText(file);
  };

  // Upload de imagens para o R2 (sequencial para manter a ordem estável).
  const handleImgFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploadingImg(true);
    try {
      for (const file of files) {
        if (!ALLOWED_IMG_TYPES.includes(file.type)) {
          toast.error(`${file.name}: ${t('skills.admin.image_type_error')}`);
          continue;
        }
        if (file.size > MAX_IMG_SIZE) {
          toast.error(`${file.name}: ${t('skills.admin.image_size_error')}`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/skills/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const { url } = res.data;
        setForm((prev) => ({
          ...prev,
          imagens: [...prev.imagens, { url, label: '', ordem: prev.imagens.length }],
        }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('skills.admin.error_upload'));
    } finally {
      setUploadingImg(false);
    }
  };

  // Upload de VÍDEO de exemplo para o R2 — entra na MESMA galeria das
  // imagens (item {tipo:'video'}); o usuário assiste embutido no detalhe.
  const handleVideoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      toast.error(`${file.name}: ${t('skills.admin.video_type_error')}`);
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error(`${file.name}: ${t('skills.admin.video_size_error')}`);
      return;
    }
    setUploadingVideo(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/skills/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { url } = res.data;
      setForm((prev) => ({
        ...prev,
        imagens: [...prev.imagens, { url, label: '', ordem: prev.imagens.length, tipo: 'video' }],
      }));
      toast.success(t('skills.admin.video_uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('skills.admin.error_upload'));
    } finally {
      setUploadingVideo(false);
    }
  };

  const setImgLabel = (index, label) => {
    setForm((prev) => ({
      ...prev,
      imagens: prev.imagens.map((img, i) => (i === index ? { ...img, label } : img)),
    }));
  };

  const moveImg = (index, dir) => {
    setForm((prev) => {
      const arr = [...prev.imagens];
      const j = index + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[index], arr[j]] = [arr[j], arr[index]];
      return { ...prev, imagens: arr };
    });
  };

  const removeImg = (index) => {
    setForm((prev) => ({ ...prev, imagens: prev.imagens.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.tipo.trim() || !form.nome.trim() || !form.conteudo.trim()) {
      toast.error(t('skills.admin.error_required'));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        tipo: form.tipo.trim(),
        nome: form.nome.trim(),
        slug: form.slug.trim() || null,
        descricao: form.descricao.trim() || null,
        categoria: form.categoria.trim() || null,
        conteudo: form.conteudo,
        instrucoes: form.instrucoes.trim(),
        video_url: form.video_url.trim(),
        tags: form.tags,
        plataformas: form.plataformas,
        imagens: form.imagens.map((img, i) => ({
          url: img.url, label: img.label || '', ordem: i,
          tipo: img.tipo === 'video' ? 'video' : 'imagem',
        })),
        ordem: Number(form.ordem) || 0,
        destaque: !!form.destaque,
        publicado: !!form.publicado,
      };
      if (editing) {
        await axios.put(`${API}/skills/${editing.id}`, payload);
        toast.success(t('skills.admin.updated'));
      } else {
        await axios.post(`${API}/skills`, payload);
        toast.success(t('skills.admin.created'));
      }
      onSaved();
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('skills.admin.error_save'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStyle = (active, color) => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 13px', borderRadius: '9px',
    fontSize: '12px', fontWeight: 600, fontFamily: 'var(--dp-font)',
    cursor: 'pointer', transition: 'all 0.2s ease', lineHeight: 1.4,
    border: active ? `1px solid ${color}66` : '1px solid var(--dp-border-subtle)',
    background: active ? `${color}14` : 'transparent',
    color: active ? color : 'var(--dp-text-tertiary)',
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={dialogContentStyleWithDropdown}>
        <div style={dialogInnerStyle}>
          <DialogHeader>
            <DialogTitle style={{
              fontSize: '16px', fontWeight: 700, color: 'var(--dp-text-primary)',
              fontFamily: 'var(--dp-font)', marginBottom: '16px',
            }}>
              {editing ? t('skills.admin.edit_skill') : t('skills.admin.new_skill')}
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '30px 0',
              justifyContent: 'center', color: 'var(--dp-text-secondary)',
              fontSize: '13px', fontFamily: 'var(--dp-font)',
            }}>
              <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
              {t('skills.detail.loading')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Tipo (livre, vira o título da seção) + Ordem */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <label style={labelStyle}>{t('skills.admin.tipo')} *</label>
                  <input
                    type="text"
                    value={form.tipo}
                    onChange={(e) => setForm((prev) => ({ ...prev, tipo: e.target.value }))}
                    onBlur={() => { if (!editing) fetchNextOrdem(form.tipo); }}
                    placeholder={t('skills.admin.tipo_placeholder')}
                    maxLength={40}
                    style={inputStyle}
                  />
                </div>
                <div style={{ width: '90px', flexShrink: 0 }}>
                  <label style={labelStyle}>{t('skills.admin.ordem')}</label>
                  <input
                    type="number"
                    value={form.ordem}
                    onChange={(e) => setForm((prev) => ({ ...prev, ordem: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
              </div>
              {existingTipos.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '-6px' }}>
                  {existingTipos.map((tp) => (
                    <button
                      key={tp}
                      type="button"
                      onClick={() => handlePickTipo(tp)}
                      style={{
                        padding: '3px 10px', borderRadius: '9999px', fontSize: '11px',
                        fontWeight: form.tipo === tp ? 600 : 400,
                        fontFamily: 'var(--dp-font)', cursor: 'pointer',
                        transition: 'all 0.2s ease', lineHeight: 1.4,
                        border: form.tipo === tp
                          ? '1px solid rgba(13, 174, 255, 0.4)'
                          : '1px solid var(--dp-border-subtle)',
                        background: form.tipo === tp ? 'rgba(13, 174, 255, 0.1)' : 'transparent',
                        color: form.tipo === tp ? 'var(--dp-blue-500)' : 'var(--dp-text-tertiary)',
                      }}
                    >
                      {tp}
                    </button>
                  ))}
                </div>
              )}
              <span style={{
                marginTop: '-8px', fontSize: '11px',
                color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
              }}>
                {t('skills.admin.tipo_hint')}
              </span>

              {/* Nome */}
              <div>
                <label style={labelStyle}>{t('skills.admin.nome')} *</label>
                <input
                  type="text"
                  value={form.nome}
                  onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
                  placeholder={t('skills.admin.nome_placeholder')}
                  style={inputStyle}
                />
              </div>

              {/* Slug */}
              <div>
                <label style={labelStyle}>{t('skills.admin.slug')}</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
                  placeholder={form.nome ? slugifyClient(form.nome) : 'ex: gerador-prompts-srt'}
                  style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: '12px' }}
                />
                <span style={{
                  display: 'block', marginTop: '4px', fontSize: '11px',
                  color: 'var(--dp-text-tertiary)', fontFamily: 'var(--dp-font)',
                }}>
                  {t('skills.admin.slug_hint')}
                </span>
              </div>

              {/* Descrição */}
              <div>
                <label style={labelStyle}>{t('skills.admin.descricao')}</label>
                <textarea
                  value={form.descricao}
                  onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
                  placeholder={t('skills.admin.descricao_placeholder')}
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.5' }}
                />
              </div>

              {/* Categoria */}
              <div>
                <label style={labelStyle}>{t('skills.admin.categoria')}</label>
                <input
                  type="text"
                  value={form.categoria}
                  onChange={(e) => setForm((prev) => ({ ...prev, categoria: e.target.value }))}
                  placeholder={t('skills.admin.categoria_placeholder')}
                  style={inputStyle}
                />
              </div>

              {/* Plataformas compatíveis */}
              <div>
                <label style={labelStyle}>{t('skills.admin.plataformas')}</label>
                <PlatformPicker
                  value={form.plataformas}
                  onChange={(next) => setForm((prev) => ({ ...prev, plataformas: next }))}
                />
              </div>

              {/* Tags */}
              <div>
                <label style={labelStyle}>{t('skills.admin.tags')}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                    }}
                    placeholder={t('skills.admin.tag_placeholder')}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button
                    type="button"
                    onClick={addTag}
                    style={{
                      padding: '0 14px', borderRadius: '8px',
                      border: '1px solid var(--dp-border-medium)', background: 'var(--dp-bg-elevated)',
                      color: 'var(--dp-text-primary)', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    <Tag size={13} />
                  </button>
                </div>
                {form.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                    {form.tags.map((tag) => (
                      <span key={tag} style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '3px 10px', borderRadius: '9999px', fontSize: '11px',
                        fontFamily: 'var(--dp-font)', color: 'var(--dp-blue-500)',
                        background: 'rgba(13, 174, 255, 0.08)', border: '1px solid rgba(13, 174, 255, 0.22)',
                      }}>
                        {tag}
                        <X size={11} style={{ cursor: 'pointer' }} onClick={() => removeTag(tag)} />
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Conteúdo + upload .md client-side */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{t('skills.admin.conteudo')} *</label>
                  <button
                    type="button"
                    onClick={() => mdInputRef.current?.click()}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '5px',
                      padding: '5px 11px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600,
                      fontFamily: 'var(--dp-font)', cursor: 'pointer',
                      border: '1px solid rgba(13, 174, 255, 0.3)', background: 'rgba(13, 174, 255, 0.06)',
                      color: 'var(--dp-blue-500)', transition: 'all 0.2s ease', lineHeight: 1.4,
                    }}
                  >
                    <FileUp size={12} />
                    {t('skills.admin.upload_md')}
                  </button>
                  <input
                    ref={mdInputRef}
                    type="file"
                    accept=".md,.markdown,.txt,text/markdown,text/plain"
                    onChange={handleMdFile}
                    style={{ display: 'none' }}
                  />
                </div>
                <textarea
                  value={form.conteudo}
                  onChange={(e) => setForm((prev) => ({ ...prev, conteudo: e.target.value }))}
                  placeholder={t('skills.admin.conteudo_placeholder')}
                  rows={10}
                  style={{
                    ...inputStyle, resize: 'vertical', lineHeight: '1.55',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    fontSize: '12px',
                  }}
                />
              </div>

              {/* Instruções de uso */}
              <div>
                <label style={labelStyle}>{t('skills.admin.instrucoes')}</label>
                <textarea
                  value={form.instrucoes}
                  onChange={(e) => setForm((prev) => ({ ...prev, instrucoes: e.target.value }))}
                  placeholder={t('skills.admin.instrucoes_placeholder')}
                  rows={4}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.55' }}
                />
              </div>

              {/* Link do tutorial em vídeo (YouTube etc. — link normal) */}
              <div>
                <label style={labelStyle}>{t('skills.admin.video_url')}</label>
                <input
                  type="url"
                  value={form.video_url}
                  onChange={(e) => setForm((prev) => ({ ...prev, video_url: e.target.value }))}
                  placeholder={t('skills.admin.video_url_placeholder')}
                  style={inputStyle}
                />
              </div>

              {/* Exemplos: imagens E vídeos upados, na MESMA galeria */}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>{t('skills.admin.images')}</label>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => imgInputRef.current?.click()}
                      disabled={uploadingImg}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '5px 11px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: uploadingImg ? 'wait' : 'pointer',
                        border: '1px solid rgba(13, 174, 255, 0.3)', background: 'rgba(13, 174, 255, 0.06)',
                        color: 'var(--dp-blue-500)', transition: 'all 0.2s ease', lineHeight: 1.4,
                        opacity: uploadingImg ? 0.6 : 1,
                      }}
                    >
                      {uploadingImg
                        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        : <Upload size={12} />}
                      {uploadingImg ? t('skills.admin.uploading') : t('skills.admin.add_images')}
                    </button>
                    <button
                      type="button"
                      onClick={() => videoInputRef.current?.click()}
                      disabled={uploadingVideo}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '5px 11px', borderRadius: '8px', fontSize: '11.5px', fontWeight: 600,
                        fontFamily: 'var(--dp-font)', cursor: uploadingVideo ? 'wait' : 'pointer',
                        border: '1px solid rgba(13, 174, 255, 0.3)', background: 'rgba(13, 174, 255, 0.06)',
                        color: 'var(--dp-blue-500)', transition: 'all 0.2s ease', lineHeight: 1.4,
                        opacity: uploadingVideo ? 0.6 : 1,
                      }}
                    >
                      {uploadingVideo
                        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        : <Upload size={12} />}
                      {uploadingVideo ? t('skills.admin.uploading') : t('skills.admin.upload_video')}
                    </button>
                  </span>
                  <input
                    ref={imgInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    onChange={handleImgFiles}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={handleVideoFile}
                    style={{ display: 'none' }}
                  />
                </div>
                {form.imagens.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {form.imagens.map((img, i) => (
                      <div key={`${img.url}-${i}`} style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '8px', borderRadius: '10px',
                        border: '1px solid var(--dp-border-subtle)', background: 'var(--dp-bg-input)',
                      }}>
                        {img.tipo === 'video' ? (
                          <video
                            src={img.url}
                            muted
                            preload="metadata"
                            style={{
                              width: '80px', height: '45px', objectFit: 'cover',
                              borderRadius: '6px', flexShrink: 0,
                              border: '1px solid var(--dp-border-subtle)', background: '#000',
                            }}
                          />
                        ) : (
                        <img
                          src={img.url}
                          alt=""
                          onError={(e) => { e.target.style.visibility = 'hidden'; }}
                          style={{
                            width: '80px', height: '45px', objectFit: 'cover',
                            borderRadius: '6px', flexShrink: 0,
                            border: '1px solid var(--dp-border-subtle)',
                          }}
                        />
                        )}
                        <input
                          type="text"
                          value={img.label || ''}
                          onChange={(e) => setImgLabel(i, e.target.value)}
                          placeholder={t('skills.admin.image_label_placeholder')}
                          style={{ ...inputStyle, flex: 1, padding: '7px 11px', fontSize: '12px' }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flexShrink: 0 }}>
                          <button type="button" onClick={() => moveImg(i, -1)} disabled={i === 0} style={{
                            border: 'none', background: 'transparent', cursor: i === 0 ? 'default' : 'pointer',
                            color: i === 0 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)', padding: 0,
                          }}>
                            <ChevronUp size={14} />
                          </button>
                          <button type="button" onClick={() => moveImg(i, 1)} disabled={i === form.imagens.length - 1} style={{
                            border: 'none', background: 'transparent',
                            cursor: i === form.imagens.length - 1 ? 'default' : 'pointer',
                            color: i === form.imagens.length - 1 ? 'var(--dp-text-tertiary)' : 'var(--dp-text-secondary)', padding: 0,
                          }}>
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImg(i)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: '26px', height: '26px', borderRadius: '7px', flexShrink: 0,
                            border: '1px solid var(--dp-border-subtle)', background: 'transparent',
                            color: 'var(--dp-text-secondary)', cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dp-red-500, #F87171)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dp-text-secondary)'; }}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Toggles + Salvar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
                paddingTop: '4px',
              }}>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, destaque: !prev.destaque }))}
                  style={toggleStyle(form.destaque, '#FBBF24')}
                >
                  <Star size={13} fill={form.destaque ? 'currentColor' : 'none'} />
                  {t('skills.admin.destaque')}
                </button>
                <button
                  type="button"
                  onClick={() => setForm((prev) => ({ ...prev, publicado: !prev.publicado }))}
                  style={toggleStyle(form.publicado, '#34D399')}
                >
                  <Eye size={13} />
                  {t('skills.admin.publicado')}
                </button>
                <span style={{ flex: 1 }} />
                <button
                  type="button"
                  className="dp-btn-cta dp-btn-cta--sm"
                  onClick={handleSave}
                  disabled={saving || uploadingImg || uploadingVideo}
                  style={{ opacity: saving || uploadingImg || uploadingVideo ? 0.6 : 1 }}
                >
                  {saving
                    ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    : <Save size={14} />}
                  {saving ? t('skills.admin.saving') : t('skills.admin.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SkillAdminModal;
